import { Column, Entity, Index } from 'typeorm';
import { BaseEntity } from './base.entity';

/**
 * `softphone_number.status` values.
 *  - `active` — provisioned + routable.
 *  - `inactive` — kept for history; won't accept new calls (admin-disabled).
 *  - `released` — number given up / removed.
 *  - `pending_port` (PD-14262) — pre-staged for an in-flight port-in. The admin
 *    surfaces treat it as a normal, fully-configurable number, but the runtime
 *    refuses to route/dial through it until the port completes (the active-only
 *    filters on the routing/caller-id paths enforce this). Flips to `active` on
 *    cutover. The row is the DURABLE carrier of all pre-staged config, decoupled
 *    from the port attempt — so a rejected-then-resubmitted port re-links to the
 *    SAME row and keeps everything the tenant configured.
 *  - `canceled_port` (PD-14262) — a pending-port number whose port was
 *    EXPLICITLY abandoned (a mere rejection does NOT cancel — config is kept for
 *    resubmission).
 */
export const SOFTPHONE_NUMBER_STATUS = {
	ACTIVE: 'active',
	INACTIVE: 'inactive',
	RELEASED: 'released',
	PENDING_PORT: 'pending_port',
	CANCELED_PORT: 'canceled_port',
} as const;

export type SoftphoneNumberStatus =
	(typeof SOFTPHONE_NUMBER_STATUS)[keyof typeof SOFTPHONE_NUMBER_STATUS];

/**
 * One row per phone number we manage on a tenant's behalf — the
 * carrier-side record that pairs a Twilio (or future RingCentral /
 * Vonage) `IncomingPhoneNumber` with our internal model.
 *
 * Distinct from `ConversationPhoneEntity` (`_conversation_phone`),
 * which exclusively maps phones to AI agents. This table holds
 * human-rep-owned numbers; a number lives in exactly one of the two
 * tables for a given tenant. The split mirrors the broader
 * call/conversation domain split — see
 * docs/claude/softphone/14-twilio-mvp-plan.md §"Three separate data stores".
 *
 * The per-rep config (assignment, ring timeout, fallback mode,
 * voicemail greeting) lives in `SoftphoneNumberConfigEntity` keyed by
 * `fk_softphone_number__id`, not on this row, because a number can
 * carry config that survives reassignment between reps.
 */
@Index('softphone_number_phoneE164_idx', ['phoneE164'], { unique: true })
@Index('softphone_number_provider_sid_idx', ['provider', 'providerNumberSid'])
@Entity('softphone_number')
export class SoftphoneNumberEntity extends BaseEntity {
	/** Provider tag — 'twilio' for v1; future: 'ringcentral' | 'vonage'. */
	@Column('varchar', { name: 'provider', length: 32 })
	public provider!: string;

	/** Provider-side resource id. For Twilio this is the `PN...` SID
	 *  returned by `incomingPhoneNumbers.create()`. */
	@Column('varchar', { name: 'providerNumberSid', length: 64 })
	public providerNumberSid!: string;

	/**
	 * The Twilio subaccount (`AC...`) this number is provisioned on, once
	 * the tenant is migrated to per-tenant subaccounts (PD-14218). The
	 * crm-api runtime scopes Twilio REST ops to it via
	 * `api.accounts(subAccountSid)...`. NULL until the tenant is cut over —
	 * until then the runtime resolves the subaccount via
	 * `PhoneLookupService.getTenantForPhone` and falls back to the master
	 * account, so a null here is a safe "still on master" signal.
	 */
	@Column('varchar', { name: 'subAccountSid', length: 64, nullable: true })
	public subAccountSid!: string | null;

	/** Canonical E.164 — the join key the matcher and TwiML route use
	 *  to look up which rep this number rings. */
	@Column('varchar', { name: 'phoneE164', length: 32 })
	public phoneE164!: string;

	/** One of {@link SOFTPHONE_NUMBER_STATUS} ('active' | 'inactive' | 'released'
	 *  | 'pending_port' | 'canceled_port'). Inactive numbers are kept for
	 *  historical correlation but won't accept new calls; pending_port numbers
	 *  are pre-staged for an in-flight port and excluded from the runtime. */
	@Column('varchar', { name: 'status', length: 16, default: 'active' })
	public status!: string;

	/**
	 * When this number was cut over from a port-in (pending_port -> active),
	 * set once by the cutover path (PD-14264). NULL for numbers that were never
	 * ported (purchased directly) or are still pending. It's the single source
	 * of truth for "the port completed, and when" — the cutover-complete
	 * notification fires off the transition, and the crm-web "place a test call"
	 * prompt shows for ~7 days after this timestamp. Deliberately separate from
	 * `_modified_date` (which any edit bumps).
	 */
	@Column('datetime', { name: 'activatedAt', nullable: true })
	public activatedAt!: Date | null;

	/** Provider-side capability tags as a sorted comma-separated list:
	 *  'voice,sms,mms'. Mirrors the underlying Twilio capabilities so
	 *  we can render the right affordances without an extra API call. */
	@Column('varchar', {
		name: 'capabilities',
		length: 64,
		nullable: true,
	})
	public capabilities!: string | null;

	/**
	 * When true, calls on this number have `record="record-from-answer-dual"`
	 * added to their `<Dial>` TwiML; Twilio captures the recording on
	 * its CDN and fires a `recording-status` webhook when complete.
	 * Defaults to OFF — recording is a tenant policy decision (legal,
	 * compliance, two-party-consent) and the admin must opt in per
	 * number via the settings UI.
	 *
	 * Both legs (rep + counterparty) are warned by the inbound
	 * preamble TwiML when this is on; the consent text is hardcoded
	 * to the safe baseline ("This call may be recorded for quality
	 * and training purposes.") for v1, configurable later.
	 */
	// type 'boolean' (not raw 'tinyint') so the mysql driver transforms
	// reads into real booleans — a raw tinyint column returns the NUMBER
	// 1/0, which silently fails strict `=== true` checks (recording never
	// engaged, PD-14797). Same tinyint(1) DDL either way.
	@Column({
		type: 'boolean',
		name: 'recordingEnabled',
		default: () => "'0'",
	})
	public recordingEnabled!: boolean;

	/**
	 * RAY BAUM §506 / E911 — the user-level emergency address row this
	 * number is currently dispatched to via Twilio's
	 * `IncomingPhoneNumber.emergencyAddressSid`. Tracked here (rather
	 * than only on Twilio's side) so we can:
	 *
	 *   1. Unregister cleanly when a number is reassigned (user A's
	 *      address is detached from the number before user B's is
	 *      attached).
	 *   2. Diff "what's in our DB vs what Twilio thinks" during a
	 *      tenant compliance audit — the dashboard reads from this
	 *      column.
	 *
	 * Null when the number has never been wired to an E911 address.
	 * The outbound-dial gate refuses dialing from any number whose
	 * assigned user has no validated `SoftphoneE911AddressEntity` row,
	 * so a null here implies "this number cannot be dialed out from"
	 * until the user completes E911 onboarding.
	 */
	@Column('int', {
		name: 'fk_softphone_e911_address__id',
		unsigned: true,
		nullable: true,
	})
	public fkSoftphoneE911AddressId!: number | null;
}
